import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta, { useJsonLd } from "@/hooks/usePageMeta";
import { siteMeta } from "@/mocks/articles";

const FORM_URL = "https://readdy.ai/api/form/dagnr3ptto0ceqqni1a0";

interface Status {
  type: "idle" | "success" | "error";
  msg: string;
}

export default function Contact() {
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });
  const [loading, setLoading] = useState(false);

  usePageMeta({
    title: "读者联络_给每日黑料提供线索、纠错或合作建议",
    description:
      "联系每日黑料编辑部：提供可核实的盘点线索、提交内容更正、洽谈转载合作或咨询订阅退订。欢迎通过读者联络页与我们沟通，核实线索我们会第一时间跟进。",
    keywords: "每日黑料,读者联络,每日黑料联系,线索投递,内容纠错,转载合作",
    canonicalPath: "/contact",
  });

  useJsonLd([
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "读者联络 - 每日黑料",
      description: "提交盘点线索、内容更正与转载合作联系",
      inLanguage: "zh-CN",
      email: siteMeta.email,
      isPartOf: { "@type": "WebSite", name: siteMeta.brand },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: "读者联络" },
      ],
    },
  ]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾蜜罐
    if ((fd.get("website_alt") as string)?.trim()) {
      setStatus({ type: "success", msg: "感谢你的留言，编辑部已收到！" });
      form.reset();
      return;
    }
    fd.delete("website_alt");

    const email = (fd.get("email") as string) || "";
    const content = (fd.get("content") as string) || "";
    if (!email) {
      setStatus({ type: "error", msg: "请填写有效的邮箱地址，方便我们回复你。" });
      return;
    }
    if (!content.trim()) {
      setStatus({ type: "error", msg: "请简要描述你的来意。" });
      return;
    }

    setLoading(true);
    setStatus({ type: "idle", msg: "" });
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
      if (res.ok && parsed?.code === "OK") {
        setStatus({ type: "success", msg: serverMsg || "留言已收到，我们会尽快与你联系。" });
        form.reset();
      } else {
        setStatus({ type: "error", msg: serverMsg || "提交失败，请稍后再试。" });
      }
    } catch {
      setStatus({ type: "error", msg: "网络异常，请稍后再试。" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6">
      {/* 面包屑 */}
      <nav className="flex items-center gap-1.5 text-xs text-foreground-600" aria-label="面包屑">
        <Link to="/" className="flex items-center gap-1 hover:text-primary-700">
          <i className="ri-home-5-line"></i>首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-primary-700">读者联络</span>
      </nav>

      <div className="grid grid-cols-1 gap-10 pt-8 lg:grid-cols-5">
        {/* 左侧说明 */}
        <div className="lg:col-span-2">
          <SectionHeading tag="CONTACT" title="给编辑部递个话" size="lg" />
          <p className="mt-4 text-[15px] leading-8 text-foreground-700">
            无论是提供一条可核实的盘点线索、指出某篇内容的疏漏，还是洽谈转载与合作，
            都可以通过右侧表单联系我们。线索核实通过后，我们会在第一时间跟进并回信。
          </p>

          <div className="mt-7 space-y-4">
            <div className="rounded-lg border border-foreground-900/10 bg-background-100/60 p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-mail-send-line text-xl"></i>
                </span>
                <div>
                  <h3 className="text-sm font-bold text-foreground-950">编辑部邮箱</h3>
                  <a href={`mailto:${siteMeta.email}`} className="mt-0.5 block text-sm text-primary-700 hover:underline">
                    {siteMeta.email}
                  </a>
                </div>
              </div>
              <p className="mt-3 text-xs leading-5 text-foreground-500">
                工作日通常在 48 小时内回复；涉及更正与法律事项我们会更快处理。
              </p>
            </div>
            <div className="rounded-lg border border-foreground-900/10 bg-foreground-950 p-5 text-background-100">
              <h3 className="flex items-center gap-2 font-heading text-lg font-black text-background-50">
                <i className="ri-shield-check-line text-accent-400"></i>
                我们承诺
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-background-200">
                {["不使用来意之外的任何个人信息", "涉及他人隐私的线索会被严格脱敏", "来函内容仅用于核实与跟进"].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <i className="ri-check-line mt-0.5 text-accent-400"></i>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* 右侧表单 */}
        <div className="lg:col-span-3">
          <form
            data-readdy-form="contact-form"
            onSubmit={onSubmit}
            noValidate
            className="rounded-lg border border-foreground-900/10 bg-background-100/60 p-6 md:p-8"
          >
            {/* 反垃圾蜜罐 */}
            <div className="frm-catch" aria-hidden="true">
              <label htmlFor="contact-website">网站地址</label>
              <input
                type="text"
                id="contact-website"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-900">
                  你的称呼
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  placeholder="如何称呼你"
                  className="h-11 w-full rounded-md border border-foreground-900/20 bg-background-50 px-3 text-sm outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-900">
                  邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  required
                  placeholder="用于回复你"
                  className="h-11 w-full rounded-md border border-foreground-900/20 bg-background-50 px-3 text-sm outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="contact-topic" className="mb-1.5 block text-sm font-medium text-foreground-900">
                来意分类
              </label>
              <div className="relative">
                <select
                  id="contact-topic"
                  name="topic"
                  className="h-11 w-full appearance-none rounded-md border border-foreground-900/20 bg-background-50 px-3 pr-10 text-sm outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                >
                  <option>提供盘点线索</option>
                  <option>内容更正 / 纠错</option>
                  <option>转载与合作</option>
                  <option>订阅与退订咨询</option>
                  <option>其他反馈</option>
                </select>
                <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-lg text-foreground-500"></i>
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="contact-content" className="mb-1.5 block text-sm font-medium text-foreground-900">
                留言内容 <span className="text-primary-500">*</span>
              </label>
              <textarea
                id="contact-content"
                name="content"
                required
                rows={6}
                maxLength={500}
                placeholder="请尽量说明来意与可核实的依据，最多 500 字"
                className="w-full resize-none rounded-md border border-foreground-900/20 bg-background-50 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
              ></textarea>
              <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-2 flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-8"
            >
              {loading ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-send-plane-line"></i>
              )}
              {loading ? "提交中…" : "提交留言"}
            </button>

            {status.type !== "idle" && (
              <p
                className={`mt-4 rounded-md px-3 py-2.5 text-sm ${
                  status.type === "success"
                    ? "bg-primary-100 text-primary-900"
                    : "bg-accent-100 text-accent-900"
                }`}
              >
                {status.msg}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}